import type { FieldType } from "@/shared/contracts/structure";
import { formatPhone } from "@/shared/format/phone";

/**
 * Reading a field's own configuration, and formatting a value.
 *
 * Kept apart from the widget REGISTRY because widgets need these and the
 * registry imports widgets — putting them together made a genuine cycle that
 * `npm run arch` caught. Pure functions with no imports of their own, which is
 * why they can sit below everything else.
 */

export interface ChoiceOption {
	readonly value: string;
	readonly label: string;
	readonly color?: string;
}

/**
 * One option, from either shape.
 *
 * Options are authored as bare strings by a person in a hurry and as objects by
 * the builder agent. Accepting both here means no caller has to know which it
 * got — and a malformed entry becomes null rather than an option labelled
 * "[object Object]".
 */
function toOption(option: unknown): ChoiceOption | null {
	if (typeof option === "string") return { value: option, label: option };
	if (!option || typeof option !== "object") return null;

	const raw = option as { value?: unknown; label?: unknown; color?: unknown };
	if (raw.value === undefined || raw.value === null) return null;

	const value = String(raw.value);
	return {
		value,
		label: raw.label === undefined ? value : String(raw.label),
		...(typeof raw.color === "string" ? { color: raw.color } : {}),
	};
}

/** Options for a choice, from the field's own config. Objects or bare strings. */
export function optionsFor(config: Record<string, unknown>): ChoiceOption[] {
	const raw = config.options;
	if (!Array.isArray(raw)) return [];
	return raw.map(toOption).filter((o): o is ChoiceOption => o !== null);
}

/**
 * The tone an option wears on screen.
 *
 * These are the design system's badge tones, named here as plain strings so a
 * resolver never has to import a component. A colour is authored freely — a name
 * ("blue"), a hex from a colour picker ("#22c55e"), or a tone itself — and it is
 * MAPPED onto the palette rather than painted literally: the tokens flip between
 * the light and the dark theme and a hex does not, so `#facc15` painted as-is is
 * a badge that half the users cannot read. Six tones instead of five because
 * five collapse "em análise" and "aguardando" into the same grey, which is the
 * opposite of what a colour was asked for.
 */
export type OptionTone = "neutral" | "success" | "warning" | "danger" | "info" | "accent";

const TONES: OptionTone[] = ["neutral", "success", "warning", "danger", "info", "accent"];

/** The vocabulary a person actually types. Anything else goes through the hex. */
const NAMED: Readonly<Record<string, OptionTone>> = {
	green: "success",
	emerald: "success",
	lime: "success",
	teal: "success",
	yellow: "warning",
	amber: "warning",
	orange: "warning",
	gold: "warning",
	red: "danger",
	rose: "danger",
	crimson: "danger",
	blue: "info",
	sky: "info",
	cyan: "info",
	purple: "accent",
	violet: "accent",
	indigo: "accent",
	fuchsia: "accent",
	pink: "accent",
	gray: "neutral",
	grey: "neutral",
	slate: "neutral",
	black: "neutral",
	white: "neutral",
};

/** Hue in degrees, and how saturated it is — enough to place a hex on the wheel. */
function hexHue(color: string): { hue: number; saturation: number } | null {
	const hex = color.replace("#", "");
	const full = hex.length === 3 ? [...hex].map((c) => c + c).join("") : hex;
	if (full.length !== 6 || !/^[0-9a-f]{6}$/i.test(full)) return null;

	const [r, g, b] = [0, 2, 4].map((i) => Number.parseInt(full.slice(i, i + 2), 16) / 255) as [
		number,
		number,
		number,
	];
	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	if (max === 0 || max === min) return { hue: 0, saturation: 0 };

	const d = max - min;
	const hue =
		max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : /* max === b */ (r - g) / d + 4;
	return { hue: (((hue * 60) % 360) + 360) % 360, saturation: d / max };
}

/** The tone a colour asks for; `neutral` whenever nothing recognisable was said. */
export function toneOf(color: string | undefined): OptionTone {
	if (!color) return "neutral";
	const said = color.trim().toLowerCase();
	if ((TONES as string[]).includes(said)) return said as OptionTone;
	if (NAMED[said]) return NAMED[said];

	const wheel = hexHue(said);
	// A washed-out colour is a grey somebody picked, not a hue they meant.
	if (!wheel || wheel.saturation < 0.18) return "neutral";
	if (wheel.hue < 20 || wheel.hue >= 330) return "danger";
	if (wheel.hue < 65) return "warning";
	if (wheel.hue < 170) return "success";
	if (wheel.hue < 250) return "info";
	return "accent";
}

/** The declared option for a stored value, if it is still declared. */
function optionFor(
	config: Record<string, unknown> | undefined,
	value: unknown,
): ChoiceOption | undefined {
	const raw = String(value);
	return optionsFor(config ?? {}).find((option) => option.value === raw);
}

/**
 * The LABEL of a choice — "Parcialmente pago", not `partially_paid`.
 *
 * The stored value stays as the fallback, with the same discipline as a broken
 * reference: an option the builder removed after records were written is still
 * what those rows hold, and blanking it would hide the mismatch instead of
 * showing it.
 */
export function optionLabel(config: Record<string, unknown> | undefined, value: unknown): string {
	return optionFor(config, value)?.label ?? String(value);
}

/** The tone of a choice, for whoever draws it — a cell, a chip, a count. */
export function optionTone(
	config: Record<string, unknown> | undefined,
	value: unknown,
): OptionTone {
	return toneOf(optionFor(config, value)?.color);
}

/** How a choice is drawn. Appearance is config, never a type (ADR-F3). */
export function displayOf(config: Record<string, unknown>): string {
	return typeof config.display === "string" ? config.display : "";
}

export interface DisplayContext {
	readonly locale: string;
	readonly currency: string;
	readonly timeZone: string;
}

/**
 * Which entity a reference points AT.
 *
 * The two core tables are named by the field TYPE — a contact and a user are not
 * dynamic entities — and everything else names its target in config.
 */
export function referenceEntityOf(type: FieldType, config?: Record<string, unknown>): string {
	if (type === "ref_contact") return "contact";
	if (type === "ref_user") return "app_user";
	return typeof config?.entity === "string" ? config.entity : "";
}

/** The cache key a resolved label is filed under. Two entities may share an id. */
export function referenceKey(entity: string, id: string): string {
	return `${entity}:${id}`;
}

/**
 * Labels already resolved for reference values, by `entity:id`.
 *
 * A `ReadonlyMap` satisfies it, and so does anything else that can answer the
 * question. Declared structurally because this file resolves NOTHING: looking an
 * id up is asynchronous and belongs to the port, while formatting is pure and
 * synchronous, and the day the two were put together `npm run arch` found the
 * cycle.
 */
export interface ReferenceLabels {
	get(key: string): string | undefined;
}

const NUMERIC = new Set<FieldType>(["currency", "number", "integer", "rating"]);

/** Um valor que o formatador do tipo sabe ler: um número que é número, uma data que existe. */
function readable(value: unknown, type: FieldType): boolean {
	if (NUMERIC.has(type)) return Number.isFinite(Number(value));
	if (type === "date") return !Number.isNaN(Date.parse(`${String(value)}T00:00:00Z`));
	if (type === "datetime") return !Number.isNaN(Date.parse(String(value)));
	return true;
}

/**
 * Formats a value using the TENANT's country and locale, never the browser's. A
 * report read in São Paulo and in Lisbon has to show the same number.
 */
export function formatValue(
	value: unknown,
	type: FieldType,
	ctx: DisplayContext,
	config?: Record<string, unknown>,
	/** Resolved by whoever drew the screen; see `ReferenceLabels`. */
	labels?: ReferenceLabels,
): string {
	if (value === null || value === undefined || value === "")
		// Uma contagem calculada de nada é 0, não um vazio: o pai sem filhos ainda não foi recontado.
		return (config?.derived as { agg?: unknown } | undefined)?.agg === "count"
			? formatValue(0, type, ctx, config, labels)
			: "—";
	// O que o tipo não lê (texto num número, uma data impossível) sai como veio — nunca "NaN" (QA 14).
	if (!readable(value, type)) return typeof value === "string" ? value : JSON.stringify(value);

	switch (type) {
		case "currency":
			return new Intl.NumberFormat(ctx.locale, {
				style: "currency",
				currency: ctx.currency,
			}).format(Number(value));
		case "number":
			return new Intl.NumberFormat(ctx.locale).format(Number(value));
		case "integer":
			// `grouping: false` exists for the year case: a model year rendered with
			// thousands separators reads as "2.023", which is wrong in a way a
			// mileage of "28.400" is not. The builder decides per field.
			return new Intl.NumberFormat(ctx.locale, {
				maximumFractionDigits: 0,
				useGrouping: config?.grouping !== false,
			}).format(Number(value));
		case "boolean":
			return value ? "✓" : "—";
		case "rating": {
			const max = Number(config?.max ?? 5);
			return `${"★".repeat(Math.min(Number(value), max))}${"☆".repeat(Math.max(0, max - Number(value)))}`;
		}
		case "date":
			// The backend sends "YYYY-MM-DD" as a plain string on purpose: building a
			// Date from it would shift the day across timezones.
			return new Intl.DateTimeFormat(ctx.locale, { timeZone: "UTC" }).format(
				new Date(`${String(value)}T00:00:00Z`),
			);
		case "datetime":
			return new Intl.DateTimeFormat(ctx.locale, {
				dateStyle: "short",
				timeStyle: "short",
				timeZone: ctx.timeZone,
			}).format(new Date(String(value)));
		case "phone":
			// Guardado em E.164 ("+5598439393443"); na tela, como se lê.
			return formatPhone(String(value)) ?? String(value);
		case "time":
			// Already wall-clock. Parsing it into a Date would apply an offset it
			// never had.
			return String(value).slice(0, 5);
		case "select":
			return optionLabel(config, value);
		case "multi_select": {
			// An empty list is an absent value, not an empty cell: `[].join()` used to
			// render a blank where every other absent value says "—".
			const chosen = Array.isArray(value) ? value : [value];
			if (chosen.length === 0) return "—";
			return chosen.map((one) => optionLabel(config, one)).join(", ");
		}
		case "file": {
			// Uma pergunta de N anexos guarda `uuid,uuid,uuid` numa coluna só (§2.6
			// #15). A célula diz QUANTOS: um uuid cru já era ruído numa coluna de
			// lista, e três lado a lado são uma linha que ninguém varre.
			const count = String(value)
				.split(",")
				.filter((id) => id.trim().length > 0).length;
			if (count === 0) return "—";
			return count === 1 ? "1 arquivo" : `${count} arquivos`;
		}
		case "signature":
			return "assinado";
		case "json":
			return JSON.stringify(value);
		case "ref_entity":
		case "ref_contact":
		case "ref_user": {
			// A column called "Responsável" showing `8f2c…` is the reason this case
			// exists. The id remains the fallback on purpose: it is what the record
			// actually holds, and blanking it would hide a reference to something
			// deleted rather than show it.
			const id = String(value);
			return labels?.get(referenceKey(referenceEntityOf(type, config), id)) ?? id;
		}
		default:
			return String(value);
	}
}
